import React, { useContext, useMemo, useState } from 'react';

import { Alert } from 'antd';

import styled from 'styled-components';

import { getMoment } from '@libs/NwMoment';
import { getNow, showDateNoTime } from '@libs/NwMoment';

//import { LEGAL_TYPES } from "@configs/LEGAL_TYPES";
import { ListContext } from '@contexts/ListContext';

import { useContactLegals } from '@hooks/API/Contact/UseContactLegals';

import ModelHeader from '@components/ContactDetails/ModelDetails/ModelHeader';
import { NwAddButton } from '@components/Gui/NwButton';
import NwDrawer from '@components/Gui/NwDrawer';
import NwIcon from '@components/Gui/NwIcon';
import LegalForm from '@components/Legal/LegalForm';
import LegalItem from '@components/Legal/LegalItem';
import YearlyLegalItem from '@components/Legal/YearlyLegalItem';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const AlertsContainer = styled.div`
    .ant-alert {
        margin-bottom: 8px;
    }
`;

const DocsWrapper = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 3rem;

    .drawer-content-yearly-docs {
        flex: 1;
    }

    .drawer-content-global-docs {
        flex: 1;
    }

    /* Su mobile non c'e' spazio per due colonne affiancate: documenti annuali sopra, generici sotto */
    @media (max-width: 720px) {
        flex-direction: column;
    }
`;

const LegalDrawer = ({ selectedContact, readOnly, onClose, modelHeader }) => {
    const { status, data: legalDocuments } = useContactLegals(selectedContact);
    const [viewMode, setViewMode] = useState('LISTING'); // LISTING, VIEWING, INSERTING
    const [currentDoc, setCurrentDoc] = useState(null);
    const [touchForm, setTouchForm] = useState(false);
    const { cachedList } = useContext(ListContext);
    const legalTypes = cachedList.legalTypes.items;

    const handleCancelEdit = () => {
        setCurrentDoc(null);
        setViewMode('LISTING');
    };

    const docs = useMemo(() => {
        const yearlyDocs = [];
        const globalDocs = [];
        if (status !== 'loading' && legalDocuments && legalDocuments.length > 0) {
            legalDocuments.forEach((doc) => {
                const docType = legalTypes.find((el) => el.Code.toLowerCase() === doc.Type.toLowerCase());
                if (docType.YearlyRequired) {
                    const docTypeInYearlyDocs = yearlyDocs.find((el) => el.Type === docType.Code);
                    if (docTypeInYearlyDocs) {
                        docTypeInYearlyDocs.Instances.push(doc);
                        if (!docTypeInYearlyDocs.Years.includes(doc.YearRef)) {
                            docTypeInYearlyDocs.Years.push(doc.YearRef);
                        }
                    } else {
                        yearlyDocs.push({
                            Type: docType.Code,
                            Instances: [doc],
                            Years: [doc.YearRef],
                        });
                    }
                } else {
                    globalDocs.push(doc);
                }
            });
        }

        return { yearlyDocs, globalDocs };
    }, [legalDocuments]);

    const hasYearlyDocs = useMemo(() => {
        if (status !== 'loading' && docs && docs.yearlyDocs) {
            return docs.yearlyDocs.length > 0;
        }
        return false;
    }, [status, docs]);

    return (
        <NwDrawer whiteBg={false} onClose={onClose} title="Legal Documents" width={hasYearlyDocs ? '60rem' : '30rem'}>
            {modelHeader && <ModelHeader modelID={selectedContact} />}
            {!readOnly && (
                <div className="drawer-toolbar-container">
                    <NwAddButton
                        primary
                        onClick={() => {
                            setViewMode('INSERTING');
                        }}
                        label="Add new document"
                    />
                </div>
            )}
            <AlertsContainer>
                {status !== 'loading' &&
                    legalDocuments.map((document) => {
                        const currentDate = getNow().hour(0).minute(0).second(0);
                        const diffDays =
                            document.ExpirationDate && currentDate.diff(getMoment(document.ExpirationDate), 'days');
                        const documentName = legalTypes.find(
                            (el) => el.Code.toLowerCase() === document.Type.toLowerCase()
                        ).Name;
                        return (
                            diffDays !== null && (
                                <div key={`alert-${document.ID}`}>
                                    {diffDays >= 0 ? (
                                        <Alert
                                            showIcon
                                            icon={<NwIcon icon={byPrefixAndName.fasl['alarm-clock']} />}
                                            message={`${documentName} expired on ${showDateNoTime(document.ExpirationDate)}`}
                                            type="error"
                                        />
                                    ) : (
                                        diffDays >= -30 && (
                                            <Alert
                                                showIcon
                                                icon={<NwIcon icon={byPrefixAndName.fasl['exclamation-triangle']} />}
                                                message={`${documentName} expiring on ${showDateNoTime(document.ExpirationDate)}`}
                                                type="warning"
                                            />
                                        )
                                    )}
                                </div>
                            )
                        );
                    })}
            </AlertsContainer>
            <div className="drawer-content standard">
                {
                    status !== 'loading' && (
                        <DocsWrapper>
                            {hasYearlyDocs && (
                                <div className="drawer-content-yearly-docs">
                                    {docs.yearlyDocs.map((item) => (
                                        <YearlyLegalItem
                                            key={item.ID}
                                            documents={item}
                                            readOnly={readOnly}
                                            contactId={selectedContact}
                                            onEditDoc={(doc) => {
                                                setCurrentDoc(doc);
                                                setViewMode('EDITING');
                                            }}
                                        />
                                    ))}
                                </div>
                            )}
                            <div className="drawer-content-global-docs">
                                {docs.globalDocs.map((item) => (
                                    <LegalItem
                                        key={item.ID}
                                        document={item}
                                        readOnly={readOnly}
                                        contactId={selectedContact}
                                        onEditDoc={() => {
                                            setCurrentDoc(item);
                                            setViewMode('EDITING');
                                        }}
                                    />
                                ))}
                            </div>
                        </DocsWrapper>
                    )

                    // (legalDocuments.map((item) => (
                    //     <LegalDocumentItem
                    //         key={item.ID}
                    //         document={item}
                    //         readOnly={readOnly}
                    //         contactId={selectedContact}
                    //         onEditDoc={() => {
                    //             setCurrentDoc(item);
                    //             setViewMode('EDITING');
                    //         }}
                    //     />
                    // )))
                }
            </div>
            {viewMode === 'INSERTING' && (
                <NwDrawer
                    touchForm={touchForm}
                    onTouchForm={setTouchForm}
                    title="Add Legal Document"
                    onClose={handleCancelEdit}
                >
                    <LegalForm ContactID={selectedContact} onCancel={handleCancelEdit} onTouchForm={setTouchForm} />
                </NwDrawer>
            )}
            {viewMode === 'EDITING' && (
                <NwDrawer
                    touchForm={touchForm}
                    onTouchForm={setTouchForm}
                    title="Edit Legal Document"
                    onClose={handleCancelEdit}
                >
                    <LegalForm
                        ContactID={selectedContact}
                        Doc={currentDoc}
                        onCancel={handleCancelEdit}
                        onTouchForm={setTouchForm}
                    />
                </NwDrawer>
            )}
        </NwDrawer>
    );
};

export default LegalDrawer;
